import React from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import { useIntl } from 'react-intl';
import { hasError, getHelpOrError } from './_common';
import Select from './Select';

const AutocompleteSelect = ({
  name,
  value,
  label,
  help,
  errors,
  options,
  required,
  onChange,
  settings,
  onBlur,
  disabled,
}) => {
  const intl = useIntl();

  const opts = {
    disableClearable: false,
    freeSolo: false,
    valueProp: 'value',
    labelProp: 'label',
    filterOptions: null,
    noOptionsText: null,
    disableInputWhenNoOptions: true,
    ...settings,
  };

  const getValue = (o) => {
    if (typeof opts.valueProp === 'function') {
      return opts.valueProp(o);
    }

    return o[opts.valueProp];
  };

  const getLabel = (o) => {
    if (typeof opts.labelProp === 'function') {
      return opts.labelProp(o);
    }

    return o[opts.labelProp];
  };

  // this prevents a bug when we have an input with
  // options fetched from the api. The input loads before the api
  // and later on the currently selected item is not properly marked.
  if (!options.length && opts.disableInputWhenNoOptions) {
    return (
      <Select
        name={name}
        value={value}
        label={label}
        help={help}
        errors={errors}
        options={[{ label: intl.formatMessage({ id: 'No options' }), value: null, disabled: true }]}
        required={required}
        onChange={null}
        onBlur={null}
        disabled={disabled}
        style={{ marginTop: '7px' }}
      />
    );
  }

  return (
    <FormControl fullWidth margin="none">
      <Autocomplete
        id={`autocomplete-${name}`}
        fullWidth
        value={value ? options.find((o) => getValue(o) === value) : null}
        options={options}
        disableClearable={opts.disableClearable}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        onBlur={onBlur}
        disabled={disabled}
        freeSolo={opts.freeSolo}
        getOptionLabel={(option) => getLabel(option)}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={label}
              required={required}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off',
              }}
              margin="dense"
              error={hasError(errors, name)}
              helperText={getHelpOrError(help, errors, name)}
            />
          );
        }}
        {...(opts.noOptionsText ? { noOptionsText: opts.noOptionsText } : {})}
        {...(opts.filterOptions ? { filterOptions: opts.filterOptions } : {})}
      />
    </FormControl>
  );
};

AutocompleteSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.node,
  help: PropTypes.node,
  errors: PropTypes.shape({}).isRequired,
  options: PropTypes.arrayOf(PropTypes.any).isRequired,
  required: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  settings: PropTypes.shape({
    disableClearable: PropTypes.bool,
    freeSolo: PropTypes.bool,
    valueProp: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    labelProp: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    filterOptions: PropTypes.func,
    noOptionsText: PropTypes.string,
    disableInputWhenNoOptions: PropTypes.bool,
  }),
  disabled: PropTypes.bool,
};

export default AutocompleteSelect;
